import { v4 as uuidv4 } from 'uuid';

import {
  type WorkspaceAgentMentionableItem,
  type WorkspaceAgentMentionSource,
  type WorkspaceAgentMentionSourceKey,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import { type WorkspaceAgentPillNodeAttrs } from '~/components/v2/components/workspace/workspace-agent-panel/tiptap/workspaceAgentPill';

/** What `@` opens on, separate from the chip order below. */
export const DEFAULT_WORKSPACE_AGENT_MENTION_SOURCE_KEY: WorkspaceAgentMentionSourceKey = 'assets';

/**
 * The registry, keyed so a source key resolves without a `find` that types as possibly missing. All
 * copy lives on the source; queries bind in `usePrivateWorkspaceAgentMentionSuggestionState`.
 */
export const WORKSPACE_AGENT_MENTION_SOURCES_BY_KEY: Record<
  WorkspaceAgentMentionSourceKey,
  WorkspaceAgentMentionSource
> = {
  assets: {
    emptyStateMessage: 'No matching assets',
    errorStateMessage: "Couldn't load assets",
    key: 'assets',
    label: 'Assets',
    listRole: 'listbox',
    noItemsMessage: 'No assets yet',
  },
  boards: {
    emptyStateMessage: 'No matching boards',
    errorStateMessage: "Couldn't load boards",
    key: 'boards',
    label: 'Boards',
    listRole: 'tree',
    noItemsMessage: 'No boards yet',
  },
  'brand-kit': {
    emptyStateMessage: 'No matching images',
    errorStateMessage: "Couldn't load brand kit",
    key: 'brand-kit',
    label: 'Contexts',
    listRole: 'listbox',
    noItemsMessage: 'No images in this context.',
  },
};

/**
 * Chip order is this order — the brand kit leads it, which is not what `@` opens on: a workspace's
 * kit is the shortest list and the one a user reaches for by name, while Assets stays the default
 * because most mentions are not brand material.
 */
export const WORKSPACE_AGENT_MENTION_SOURCES: readonly WorkspaceAgentMentionSource[] = [
  WORKSPACE_AGENT_MENTION_SOURCES_BY_KEY['brand-kit'],
  WORKSPACE_AGENT_MENTION_SOURCES_BY_KEY.assets,
  WORKSPACE_AGENT_MENTION_SOURCES_BY_KEY.boards,
];

/** Follow-the-hits walks this, so a new source is a registry row rather than a switcher case. */
export const WORKSPACE_AGENT_MENTION_SOURCE_KEYS: readonly WorkspaceAgentMentionSourceKey[] =
  WORKSPACE_AGENT_MENTION_SOURCES.map((source) => source.key);

/** Mints a fresh pill id per call — call once, on pick, never during render. */
export const toWorkspaceAgentPillNodeAttrs = (item: WorkspaceAgentMentionableItem): WorkspaceAgentPillNodeAttrs => {
  /** Client-generated per insertion, never derived from the entity. */
  const id = uuidv4();

  if (item.kind === 'board') {
    return {
      assetId: null,
      assetType: null,
      boardId: item.boardId,
      contextSourceFieldValueId: null,
      contextSourceId: null,
      durableWireEntityType: null,
      ext: null,
      id,
      label: item.title,
      thumbnailUrl: item.coverImageUrl,
      type: 'board',
      versionId: null,
    };
  }

  if (item.kind === 'contextSourceFieldValue') {
    return {
      // No asset on the wire: the backend resolves the value against the kit itself.
      assetId: null,
      assetType: null,
      boardId: null,
      contextSourceFieldValueId: item.contextSourceFieldValueId,
      contextSourceId: item.contextSourceId,
      durableWireEntityType: null,
      ext: null,
      id,
      label: item.title,
      thumbnailUrl: item.thumbnailUrl,
      type: 'contextSourceFieldValue',
      versionId: null,
    };
  }

  return {
    assetId: item.assetId,
    assetType: item.assetType,
    boardId: null,
    contextSourceFieldValueId: null,
    contextSourceId: null,
    durableWireEntityType: null,
    ext: item.ext || null,
    id,
    label: item.title,
    thumbnailUrl: item.thumbnailUrl,
    type: 'asset',
    versionId: item.versionId,
  };
};
